/**
 * Refund Dialog Component
 * Used in Orders page and POS to process refunds with eTIMS credit note submission
 */
import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  ArrowLeftRight, AlertTriangle, Loader2, Receipt, CreditCard,
  Banknote, Smartphone, Plus, Minus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useAuthStore } from "@/store/useStore";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Refund reason codes (KRA eTIMS standard)
const REFUND_REASONS = [
  { code: "01", description: "Wrong Entry", icon: "⚠️" },
  { code: "02", description: "Goods Returned", icon: "📦" },
  { code: "03", description: "Goods Damaged", icon: "💔" },
  { code: "04", description: "Price Adjustment", icon: "💰" },
  { code: "05", description: "Customer Complaint", icon: "😞" },
  { code: "06", description: "Duplicate Entry", icon: "📋" },
  { code: "07", description: "Other", icon: "📝" }
];

// Refund methods
const REFUND_METHODS = [
  { value: "original", label: "Same as Original Payment", icon: CreditCard },
  { value: "cash", label: "Cash Refund", icon: Banknote },
  { value: "mpesa", label: "M-Pesa Refund", icon: Smartphone }
];

export default function RefundDialog({ 
  open, 
  onOpenChange, 
  order, 
  onRefundComplete 
}) {
  const { token } = useAuthStore();
  const [reasonCode, setReasonCode] = useState("02");
  const [reasonNotes, setReasonNotes] = useState("");
  const [refundMethod, setRefundMethod] = useState("original");
  const [processing, setProcessing] = useState(false);
  const [restock, setRestock] = useState(true);
  const [qtys, setQtys] = useState({}); // line index -> qty to refund

  const orderItems = useMemo(() => (order?.items || []), [order]);

  // Initialize quantities (default: full quantity of every line) whenever the dialog opens
  useEffect(() => {
    if (open && order) {
      const init = {};
      (order.items || []).forEach((it, idx) => {
        init[idx] = it.quantity || 1;
      });
      setQtys(init);
      setRestock(true);
      setReasonCode("02");
      setReasonNotes("");
      setRefundMethod("original");
    }
  }, [open, order]);

  const setLineQty = (idx, value, max) => {
    const q = Math.max(0, Math.min(max, Number(value) || 0));
    setQtys((prev) => ({ ...prev, [idx]: q }));
  };

  const unitPrice = (it) => it.price ?? it.unit_price ?? 0;

  const isFullRefund = orderItems.length > 0 &&
    orderItems.every((it, idx) => (qtys[idx] ?? 0) === (it.quantity || 1));

  const refundTotal = isFullRefund
    ? (order?.total || 0)
    : orderItems.reduce((sum, it, idx) => sum + unitPrice(it) * (qtys[idx] ?? 0), 0);

  const anySelected = orderItems.reduce((n, _it, idx) => n + (qtys[idx] ?? 0), 0) > 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const handleRefund = async () => {
    if (!order) return;
    if (!anySelected) {
      toast.error("Select at least one item to refund");
      return;
    }

    try {
      setProcessing(true);

      // Only send line items for a partial refund; omit for a full refund
      const items = isFullRefund
        ? null
        : orderItems
            .map((it, idx) => ({
              product_id: it.product_id,
              variant_id: it.variant_id || null,
              quantity: qtys[idx] ?? 0,
            }))
            .filter((l) => l.quantity > 0);

      const response = await fetch(`${API_URL}/api/orders/${order.id}/refund`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          reason_code: reasonCode,
          reason: reasonNotes || REFUND_REASONS.find(r => r.code === reasonCode)?.description,
          refund_method: refundMethod,
          restock,
          items,
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Build success message
        let message = `Refund processed for ${formatCurrency(data.refund_amount)}`;
        
        if (data.etims_credit_note_submitted) {
          message += ` | eTIMS Credit Note #${data.etims_credit_note_invc_no}`;
          toast.success(message, { duration: 5000 });
        } else if (data.etims_credit_note_skipped) {
          toast.success(message);
          toast.info("eTIMS credit note skipped: " + (data.etims_skip_reason || "Not configured"));
        } else if (data.etims_credit_note_error) {
          toast.success(message);
          toast.warning("eTIMS credit note failed: " + data.etims_credit_note_error);
        } else {
          toast.success(message);
        }
        
        onOpenChange(false);
        if (onRefundComplete) {
          onRefundComplete(data);
        }
      } else {
        toast.error(data.detail || "Failed to process refund");
      }
    } catch (error) {
      toast.error("Failed to process refund");
    } finally {
      setProcessing(false);
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case "cash": return "Cash";
      case "card": return "Card";
      case "mpesa": return "M-Pesa (STK)";
      case "mpesa_c2b": return "M-Pesa (Paybill)";
      default: return method;
    }
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5 text-orange-600" />
            Process Refund
          </DialogTitle>
          <DialogDescription>
            Refund order {order.order_number || order.invoice_number}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 overflow-y-auto flex-1 pr-1 -mr-1">
          {/* Order Summary */}
          <Card className="bg-muted/50">
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground">Order</p>
                  <p className="font-medium">{order.order_number || order.invoice_number}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total</p>
                  <p className="font-bold text-lg">{formatCurrency(order.total)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Customer</p>
                  <p className="font-medium">{order.customer_name || "Walk-in"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Payment</p>
                  <p className="font-medium">{getPaymentMethodLabel(order.payment_method)}</p>
                </div>
                {order.etims_invc_no && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">eTIMS Invoice</p>
                    <p className="font-medium text-green-600">#{order.etims_invc_no}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Items to refund (partial refund support) */}
          {orderItems.length > 0 && (
            <div className="space-y-2" data-testid="refund-items-section">
              <Label>Items to Refund</Label>
              <div className="border rounded-md divide-y max-h-52 overflow-y-auto">
                {orderItems.map((it, idx) => {
                  const maxQ = it.quantity || 1;
                  const q = qtys[idx] ?? 0;
                  return (
                    <div key={idx} className="flex items-center gap-2 p-2" data-testid={`refund-item-${idx}`}>
                      <Checkbox
                        checked={q > 0}
                        onCheckedChange={(c) => setLineQty(idx, c ? maxQ : 0, maxQ)}
                        data-testid={`refund-item-check-${idx}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {it.name}{it.variant_label ? ` · ${it.variant_label}` : ""}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(unitPrice(it))} · sold {maxQ}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button type="button" size="icon" variant="outline" className="h-7 w-7"
                          onClick={() => setLineQty(idx, q - 1, maxQ)} disabled={q <= 0}
                          data-testid={`refund-item-minus-${idx}`}>
                          <Minus className="h-3 w-3" />
                        </Button>
                        <span className="w-6 text-center text-sm font-medium" data-testid={`refund-item-qty-${idx}`}>{q}</span>
                        <Button type="button" size="icon" variant="outline" className="h-7 w-7"
                          onClick={() => setLineQty(idx, q + 1, maxQ)} disabled={q >= maxQ}
                          data-testid={`refund-item-plus-${idx}`}>
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {!isFullRefund && (
                <p className="text-xs text-orange-600">Partial refund — only the selected items will be refunded.</p>
              )}
            </div>
          )}

          {/* Return to stock toggle */}
          <div className="flex items-start gap-2 rounded-md border p-3">
            <Checkbox
              id="restock"
              checked={restock}
              onCheckedChange={(c) => setRestock(!!c)}
              data-testid="refund-restock-checkbox"
            />
            <div className="grid gap-0.5">
              <Label htmlFor="restock" className="cursor-pointer">Return items to stock</Label>
              <p className="text-xs text-muted-foreground">
                Uncheck for damaged goods that should not go back into inventory.
              </p>
            </div>
          </div>

          {/* Refund Reason */}
          <div className="space-y-2">
            <Label>Reason for Refund *</Label>
            <Select value={reasonCode} onValueChange={setReasonCode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {REFUND_REASONS.map((reason) => (
                  <SelectItem key={reason.code} value={reason.code}>
                    <span className="flex items-center gap-2">
                      <span>{reason.icon}</span>
                      <span>{reason.code} - {reason.description}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Additional Notes */}
          <div className="space-y-2">
            <Label>Additional Notes (Optional)</Label>
            <Textarea
              value={reasonNotes}
              onChange={(e) => setReasonNotes(e.target.value)}
              placeholder="Enter additional details about this refund..."
              rows={2}
            />
          </div>
          
          {/* Refund Method */}
          <div className="space-y-2">
            <Label>Refund Method</Label>
            <RadioGroup value={refundMethod} onValueChange={setRefundMethod} className="space-y-2">
              {REFUND_METHODS.map((method) => (
                <div key={method.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={method.value} id={method.value} />
                  <Label htmlFor={method.value} className="flex items-center gap-2 cursor-pointer">
                    <method.icon className="h-4 w-4 text-muted-foreground" />
                    {method.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          
          {/* eTIMS Notice */}
          {order.etims_submitted && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
              <div className="flex gap-2">
                <Receipt className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-800">eTIMS Credit Note</p>
                  <p className="text-blue-700">
                    A credit note will be automatically submitted to KRA eTIMS for this refund.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Warning */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm">
            <div className="flex gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Warning</p>
                <p className="text-yellow-700">
                  This action cannot be undone. {formatCurrency(refundTotal)} will be refunded
                  {restock ? " and the selected items returned to stock." : " (items will NOT be returned to stock)."}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <DialogFooter className="flex-shrink-0 border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={processing}>
            Cancel
          </Button>
          <Button 
            onClick={handleRefund} 
            disabled={processing || !anySelected}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="refund-submit-btn"
          >
            {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Refund {formatCurrency(refundTotal)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Export refund reasons for use elsewhere
export { REFUND_REASONS };
